<template>
  <v-container fluid class="fill-height pa-0">
    <v-row class="flex-grow-1">
      <v-col cols="12" class="d-flex flex-column">
        <v-card
          :loading="loading"
          class="bg-grey-lighten-4 flex-grow-1 d-flex flex-column"
        >
          <v-toolbar color="primary" density="comfortable">
            <v-toolbar-title class="text-white">
              {{ title }}
            </v-toolbar-title>
            <v-spacer />
            <div class="d-flex ga-2">
              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon
                    variant="text"
                    :disabled="!isTaskRoute"
                    @click="cancel"
                  >
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Fermer</span>
              </v-tooltip>

              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="saveTask">
                    <v-icon>mdi-content-save-all</v-icon>
                  </v-btn>
                </template>
                <span>Sauvegarder</span>
              </v-tooltip>

              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon
                    variant="text"
                    :disabled="isAddSubtaskDisabled"
                    @click="addSubtask"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Ajouter une tâche</span>
              </v-tooltip>

              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon
                    variant="text"
                    :disabled="gotoTaskListBtnDisabled"
                    @click="gotoTaskList"
                  >
                    <v-icon>mdi-hammer-wrench</v-icon>
                  </v-btn>
                </template>
                <span>Naviguer vers la Tournée</span>
              </v-tooltip>

              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon
                    variant="text"
                    :disabled="isDeleteTaskDisabled"
                    @click="deleteTsk"
                  >
                    <v-icon>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
                <span>Supprimer</span>
              </v-tooltip>
            </div>
          </v-toolbar>

          <v-card-text>
            <v-container fluid>
              <v-row dense>
                <v-col md="6" cols="12">
                  <v-text-field label="Sujet" v-model="localTask.subject" />
                </v-col>

                <v-col md="6" cols="12">
                  <v-text-field
                    v-if="isLaverieFieldDisabled"
                    label="Laverie"
                    :model-value="localTask.numeroDeContrat"
                    readonly
                  />
                  <v-autocomplete
                    v-else
                    label="Laverie"
                    v-model="localTask.laverieId"
                    :items="laveries"
                    item-title="numeroDeContrat"
                    item-value="id"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col md="6" cols="12">
                  <v-text-field
                    v-if="isCodeTourneeFieldDisabled"
                    label="Code Tournée"
                    :model-value="codeTournee"
                    readonly
                  />
                  <v-autocomplete
                    v-else
                    label="Code Tournée"
                    v-model="localTask.taskListId"
                    :items="taskLists"
                    item-title="label"
                    item-value="id"
                    clearable
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <v-autocomplete
                    label="Type d'activité"
                    :items="taskTypes"
                    v-model="localTask.taskType"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col md="6" cols="12">
                  <v-autocomplete
                    label="Status"
                    :items="taskStatuses"
                    v-model="localTask.taskStatus"
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <v-autocomplete
                    label="Priorité"
                    :items="taskPriorities"
                    v-model="localTask.priority"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12">
                  <v-textarea label="Observation" v-model="localTask.observation" />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12">
                  <SubtaskList
                    :subtasks="localTask.subtasks"
                    @remove="removeSubtask"
                    @update="updateSubtask"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="addSubtaskModal" max-width="600" persistent>
      <v-card>
        <v-card-title>{{ modalTitle }}</v-card-title>
        <v-card-text>{{ modalText }}</v-card-text>
        <v-card-text>
          <v-textarea label="Description" v-model="subtask.name" counter="255" />
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="saveSubtask">Confirm</v-btn>
          <v-btn color="red" @click="cancelAddSubtask">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmDialog
      v-model="deleteSubtaskDialog"
      dialog-title="Suppression"
      dialog-text="Voulez vous supprimer cette tâche?"
      @confirm="onConfirmSubtaskRemoval"
      @cancel="onCancelSubtaskRemoval"
    />

    <ConfirmDialog
      v-model="deleteSubtaskDialog"
      dialog-title="Suppression"
      dialog-text="Voulez vous supprimer cette tâche?"
      @confirm="onConfirmSubtaskRemoval"
      @cancel="onCancelSubtaskRemoval"
    />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { Priority, Task } from "@/types";
import { Subtask } from "@/types";
import { TaskList } from "@/types";
import { Laverie } from "@/types";
import { TaskType, TaskStatus, TaskListStatus } from "@/types";
import { useAppStore } from "@/store/app";
import { getTaskById, postTask, putTask, deleteTask } from "@/utils/task-api";
import { getAllLaveries } from "@/utils/laverie-api";
import { getTaskListById, getFilteredTaskLists } from "@/utils/tasklist-api";
import SubtaskList from "@/components/SubtaskList.vue";

const props = defineProps<{
  task?: Task | null;
  cardClass?: string;
}>();

const emit = defineEmits([
  "update:task",
  "delete:task",
  "status-changed",
  "name-changed",
]);

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();

const modalTitle = "Ajout";
const modalText = "Ajouter une tâche à cette activité";

const localTask = ref<Task>({ subtasks: [] } as Task);
const selectedSubtask = ref<Subtask | null>(null);

const loading = ref(false);
const deleteSubtaskDialog = ref(false);
const deleteTaskDialog = ref(false);
const addSubtaskModal = ref(false);

const title = ref("");
const subtask = ref<Subtask>({} as Subtask);

const taskLists = ref<TaskList[]>([]);
const laveries = ref<Laverie[]>([]);
const taskTaskList = ref<TaskList | null>(null);

const taskTypes = Object.values(TaskType);
const taskStatuses = Object.values(TaskStatus);
const taskPriorities = Object.values(Priority);

const isTaskRoute = computed(() => ["task", "newtask"].includes(String(route.name)));

const isCreatingNewTask = () => route.name === "newtask";
const isAddSubtaskDisabled = computed(() => !localTask.value.id);
const isDeleteTaskDisabled = computed(() => !props.task);
const gotoTaskListBtnDisabled = computed(
  () => !isTaskRoute.value || !localTask.value.taskListId
);

const isLaverieFieldDisabled = computed(() => !!localTask.value.id);
const isCodeTourneeFieldDisabled = computed(() => !!props.task);
const codeTournee = computed(() => taskTaskList.value?.codeTournee);

watch(() => props.task, cloneTask, { immediate: true });
watch(
  () => localTask.value.taskStatus,
  (s) => emit("status-changed", s)
);
watch(() => localTask.value.laverieId, onLaverieChanged);

function onCancelSubtaskRemoval() {
  deleteSubtaskDialog.value = false;
  selectedSubtask.value = null;
}

function cloneTask() {
  if (props.task) {
    localTask.value = JSON.parse(JSON.stringify(props.task));
    localTask.value.subtasks ||= [];
  }
}

function addSubtask() {
  addSubtaskModal.value = true;
}

function saveSubtask() {
  localTask.value.subtasks.push(subtask.value);
  subtask.value = {} as Subtask;
  addSubtaskModal.value = false;
  saveTask();
}

function removeSubtask(item: Subtask) {
  deleteSubtaskDialog.value = true;
  selectedSubtask.value = item;
}

function updateSubtask(updated: Subtask) {
  const index = localTask.value.subtasks.findIndex(
    (s) => s === updated || s.id === updated.id
  );
  if (index !== -1) {
    localTask.value.subtasks[index] = updated;
    saveTask();
  }
}

function onConfirmSubtaskRemoval() {
  deleteSubtaskDialog.value = false;

  if (!selectedSubtask.value?.id) {
    return;
  }

  localTask.value.subtasks = localTask.value.subtasks.filter(
    (s) => s.id !== selectedSubtask.value!.id
  );

  saveTask();
}

function cancelAddSubtask() {
  addSubtaskModal.value = false;
}

async function saveTask() {
  loading.value = true;
  try {
    if (!localTask.value.id) {
      const t = await postTask(localTask.value);
      localTask.value = t;
      emit("update:task", t);
      router.replace({ name: "task", params: { id: t.id } });
      title.value = "Editer l'activité";
    } else {
      await putTask(localTask.value);
      emit("update:task", localTask.value);
    }
    appStore.sendSuccessNotice("Task saved.");
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function deleteTsk() {
  deleteTaskDialog.value = true;
}

async function onConfirmTaskDeletion() {
  deleteTaskDialog.value = false;
  if (props.task?.id) {
    await deleteTask([props.task.id as string]);
  }
  emit("delete:task", props.task);
}

function onCancelTaskDeletion() {
  deleteTaskDialog.value = false;
}

function cancel() {
  router.push({ name: "tasks" });
}

function gotoTaskList() {
  router.push(`/tasklists/${localTask.value.taskListId}`);
}

async function onLaverieChanged(id: string) {
  if (localTask.value.id) return;
  const lav = laveries.value.find((l) => l.id === id);
  if (lav) emit("name-changed", lav.name);
}

onMounted(async () => {
  title.value = isCreatingNewTask() ? "Nouvelle activité" : "Editer l'activité";

  if (!props.task && route.params.id) {
    localTask.value = await getTaskById(route.params.id as string);
  }

  const [lists, lavs] = await Promise.all([
    getFilteredTaskLists([TaskListStatus.A_FAIRE, TaskListStatus.EN_COURS]),
    getAllLaveries(),
  ]);

  taskLists.value = lists;
  laveries.value = lavs;
});
</script>
<style scoped>
.task-form-root {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
}
</style>
